import type { IoTDataPoint, StationTelemetryOverlayEntry } from '@/features/realtime/types/realtime.types';
import { pickTelemetryOverlay } from '@/features/realtime/utils/pickTelemetryOverlay';
import {
  PRIMARY_ROUTING_ORDER,
  rainfallWindowCodeFromDurationH,
} from '@/features/realtime/utils/stationSensorBindings';
function pickPrimaryRoutingKey(station: IoTDataPoint): string | undefined {
  const keys = Object.keys(station.readingsByRoutingKey ?? {});
  for (const rk of PRIMARY_ROUTING_ORDER) {
    if (keys.includes(rk)) return rk;
  }
  if (station.sensorChannelCode) return station.sensorChannelCode;
  return keys[0];
}

/** Đồng bộ giá trị WS (routing_key) vào readingsByChannelId để ngưỡng đọc đúng kênh. */
function syncChannelReadingsFromRoutingKeys(
  channelCodeById: Record<string, string> | undefined,
  readingsByChannelId: Record<string, number>,
  readingsByRoutingKey: Record<string, number>,
): void {
  if (!channelCodeById) return;
  for (const [channelId, rk] of Object.entries(channelCodeById)) {
    const v = readingsByRoutingKey[rk];
    if (v != null && Number.isFinite(v)) readingsByChannelId[channelId] = v;
  }
}

/** Merge WS overlay vào trạm: cập nhật readings theo kênh, value primary, timestamp. */
export function mergeStationWithTelemetryOverlay(
  station: IoTDataPoint,
  overlay: StationTelemetryOverlayEntry,
): IoTDataPoint {
  const readingsByChannelId = { ...(station.readingsByChannelId ?? {}) };
  const readingsByRoutingKey = { ...(station.readingsByRoutingKey ?? {}) };

  if (overlay.readingsByChannelId) {
    Object.assign(readingsByChannelId, overlay.readingsByChannelId);
  }
  if (overlay.readingsByRoutingKey) {
    Object.assign(readingsByRoutingKey, overlay.readingsByRoutingKey);
  }
  syncChannelReadingsFromRoutingKeys(
    station.channelCodeById,
    readingsByChannelId,
    readingsByRoutingKey,
  );

  const primaryRk = pickPrimaryRoutingKey({
    ...station,
    readingsByRoutingKey:
      Object.keys(readingsByRoutingKey).length > 0
        ? readingsByRoutingKey
        : station.readingsByRoutingKey,
  });
  const primaryVal =
    (primaryRk && readingsByRoutingKey[primaryRk] != null
      ? readingsByRoutingKey[primaryRk]
      : undefined) ??
    (station.sensorChannelId && readingsByChannelId[station.sensorChannelId] != null
      ? readingsByChannelId[station.sensorChannelId]
      : undefined) ??
    overlay.value;

  const lastRainfallWindowCode =
    overlay.lastRainfallDurationH != null
      ? rainfallWindowCodeFromDurationH(overlay.lastRainfallDurationH)
      : station.lastRainfallWindowCode;

  return {
    ...station,
    value: primaryVal,
    timestamp: overlay.timestamp,
    lastUpdated: overlay.lastUpdated,
    ...(lastRainfallWindowCode ? { lastRainfallWindowCode } : {}),
    ...(Object.keys(readingsByChannelId).length > 0 ? { readingsByChannelId } : {}),
    ...(Object.keys(readingsByRoutingKey).length > 0 ? { readingsByRoutingKey } : {}),
  };
}

export function mergeStationTelemetryFromMap(
  station: IoTDataPoint,
  telemetryByStationId: Record<string, StationTelemetryOverlayEntry>,
): IoTDataPoint {
  const overlay = pickTelemetryOverlay(station, telemetryByStationId);
  if (!overlay) return station;
  return mergeStationWithTelemetryOverlay(station, overlay);
}
